import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ConfirmModelComponent } from '../common/confirm-model/confirm-model.component';
import { FlowSheetService } from '../services/flowsheet-service';
import { GlobalService } from '../services/global.service';
import { FilterModel } from '../models/filter/filter-request.model';
import { FilterResponseModel } from '../models/filter/filter-response.model';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FilterFieldTypes } from '../common/common-constants';
import { Globals } from '../models/globals.model';
import { TenantService } from '../services/tenant.service';


@Component({
  selector: 'app-flowsheet',
  templateUrl: './flowsheet.component.html',
  styleUrls: ['./flowsheet.component.css']
})
export class FlowSheetComponent implements OnInit, OnDestroy {
  resourceList: any = [];
  expandedIndex: any;
  isLoading = true;
  modelErrorMessage = 'Error';
  modelDetails: any;
  flowsheetsListDetails: MatTableDataSource<any>;
  displayedColumns: string[] = ['Add', 'flowSheetName', 'flowSheetDescription', 'lastUpdatedTimeStamp', 'lastUpdatedBy', 'Action'];
  isLinear = false;
  flowSheetIdToDelete: number = 0;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  filterList: FilterModel[] = [];
  selectedFilters: FilterResponseModel[] = [];
  masterData: any;
  @ViewChild('popOver') public popover: NgbPopover;
  public destroyed = new Subject<any>();
  constructor(
    public _router: Router,
    public globals: GlobalService,
    public global: Globals,
    public flowSheetService: FlowSheetService,
    private readonly tenantService:TenantService,
    public dialog: MatDialog) {
    this._router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      takeUntil(this.destroyed)
    ).subscribe(() => {
      this.initialize();
    });
    const emptyList: any[] = [];
    this.flowsheetsListDetails = new MatTableDataSource(emptyList);
  }

  initialize() {
    this.expandedIndex = -1;
    let serviceTenant:any = this.tenantService.getCurrentTenant();
    if (serviceTenant?.tenantId && serviceTenant.tenantId > 0) {
      this.getFlowsheetsList();
    }    
  }

  ngOnInit() {
    // we cant remove empty 'ngOnInit'
  }


  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  getFlowsheetsList() {
    this.selectedFilters = [];
    this.isLoading = true;
    this.flowSheetService.getFlowSheetsList().subscribe((data: any): void => {
      if (data && data !== null && data.length > 0) {
        this.masterData = [...data];
        this.flowsheetsListDetails = new MatTableDataSource(data);
        this.isUserAuthorized();
        this.sort.sort(({ id: 'flowSheetName', start: 'asc' }) as MatSortable);
        this.flowsheetsListDetails.sort = this.sort;
        this.flowsheetsListDetails.paginator = this.paginator;
      }
      else{
        this.flowsheetsListDetails.data = [];
      }
      this.isLoading = false;
      this.filterList = [];
      this.createFilterList();
    }, (error: Error) => {
      this.modelErrorMessage = 'Error in fetching Flowsheets List, more details: ' + error.message;
      this.isLoading = false;
    });
  }


   /**
    * checks if user is authorized or not and marks flag as true and false
    */
   isUserAuthorized() {
    for (const element of this.flowsheetsListDetails.filteredData) {
      if (element &&
          element.createdBy !== this.globals.loggedInUser &&
          this.global.rbacName !== 'Platform Admin') {
        element.isUnauthorized = true;
      } else {
        element.isUnauthorized = false;
      }
    }
  }

  deleteFlowSheet(flowSheetId) {
    this.flowSheetIdToDelete = flowSheetId;
    let dataToPass = {
      title: 'Delete',
      isDelete: true,
      message: 'Do you really want to delete it?'
    };
    this.globals.openModelWindow(this.deleteOnConfirm, this, ConfirmModelComponent, '450px', 'confirm-modalbox', dataToPass);
  }

  deleteOnConfirm(thisRef: FlowSheetComponent) {
    thisRef.isLoading = true;
    thisRef.flowSheetService.deleteFlowSheet(thisRef.flowSheetIdToDelete).subscribe((result: any): void => {
      if (result == "Success") {
        thisRef.getFlowsheetsList();
      }
      thisRef.isLoading = false;
      thisRef.flowSheetIdToDelete = 0;
    }, (error: Error) => {
      thisRef.modelErrorMessage = 'Error in deleting FlowSheet, more details: ' + error.message;
      thisRef.isLoading = false;
      thisRef.flowSheetIdToDelete = 0;
    });
  }

  /**
   * Filters data based on input text
   * @param filterValue
   */
  applyFilter(filterValue: string) {
    filterValue = filterValue.trim().toLowerCase(); // Remove whitespace and MatTableDataSource defaults to lowercase matches
    this.flowsheetsListDetails.filter = filterValue;
  }

  /**Creates data for filter list */
  createFilterList() {
    for (let a = 0; a < this.displayedColumns.length - 1; a++) {
      let tempFilter: FilterModel = new FilterModel();
      let unique;
      tempFilter.fieldName = this.globals.replaceDisplayText(this.displayedColumns[a]);
      tempFilter.fieldType = FilterFieldTypes.text;
      if (this.displayedColumns[a] == "flowSheetName") {
        unique = Array.from(new Set(this.flowsheetsListDetails.data.map(a => a.flowSheetName)));
      } else if (this.displayedColumns[a] == "flowSheetDescription") {
        unique = Array.from(new Set(this.flowsheetsListDetails.data.map(a => a.flowSheetDescription)));
      } else if (this.displayedColumns[a] == "lastUpdatedTimeStamp") {
        unique = Array.from(new Set(this.flowsheetsListDetails.data.map(a => a.lastUpdatedTimeStamp)));
      } else if (this.displayedColumns[a] == "lastUpdatedBy") {
        unique = Array.from(new Set(this.flowsheetsListDetails.data.map(a => a.lastUpdatedBy)));
      }
      tempFilter.values = unique;
      this.filterList.push(tempFilter);
    }
    this.filterList.splice(0, 1);
  }

  /**
  * Returns data from child filter component
  * @param event
  */
  filterResponse(event) {
    let filterResponse: FilterResponseModel = event.filterResponse;
    this.flowsheetsListDetails.data = this.globals.conditionalDataFiltering(filterResponse, this.flowsheetsListDetails.data);
    this.selectedFilters.push(filterResponse);
    if (this.popover.isOpen()) this.popover.close();
  }

  /**
   * Removes that filter
   * @param selectedFilter
   */
  remove(selectedFilter: FilterResponseModel): void {
    const index = this.selectedFilters.indexOf(selectedFilter);    
    if (index >= 0) {
      this.selectedFilters.splice(index, 1);
      this.flowsheetsListDetails.data = this.masterData.slice(0);
      if (this.selectedFilters.length > 0) {
        for (let element of this.selectedFilters) {
          this.flowsheetsListDetails.data = this.globals.conditionalDataFiltering(element, this.flowsheetsListDetails.data);
        }
      }
    }
  }

}
